import React, { useCallback, useRef } from 'react';

import {
  LTBox,
  LTIconButton,
  LTLoader,
  LTTestStatusLabel,
  LTText,
  LTTooltip
} from '@lambdatestincprivate/lt-components';
import { themeGet } from '@primer/react';
import capitalize from 'lodash/capitalize';
import styled, { keyframes } from 'styled-components';

import { TestStatusNameMap } from '../../constants/test';
import { FlakyTestIcon } from '../../assets/flaky-test';
import phone from '../../assets/phone.svg';
import resolutionIcon from '../../assets/resolution.svg';
import NewTab from '../../assets/newTab';
import { getStatusIconAndClassPrefix } from '../../utils/statusOptions';
import { getBrowserIcon, getOsIcon } from '../../utils/utilityFunctions';
import {
  convertTimeTaken,
  formatTimestampToDateAndTime
} from '../../utils/dateTimeUtils';

const ColumnRowWrapper = styled.div`
  border: 1px solid ${themeGet('colors.border.default')};
  background-color: ${themeGet('colors.bg.muted')};
  border-bottom: none;
  display: flex;
  font-size: 12px;
  &:last-child {
    border-bottom: 1px solid ${themeGet('colors.border.default')};
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
  }
  &:first-child {
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
  }
`;

const DataRowWrapper = styled(ColumnRowWrapper)`
  background-color: ${({ $isSelectedTest }) =>
    $isSelectedTest
      ? themeGet('colors.bg.muted')
      : themeGet('colors.bg.default')};
  &:first-child {
    border-top: 0px;
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
  }
`;

const Cell = styled.div`
  padding: 6px 12px;
  display: flex;
  align-items: center;
  word-break: break-word;
  border-right: 1px solid ${themeGet('colors.border.default')};
  flex-wrap: wrap;
  white-space: normal;
  line-height: 1.4;
  min-width: 0;
  overflow: hidden;
  &:last-child {
    border-right: none;
  }
`;

const ColumnCell = styled(Cell)`
  font-weight: 600;
  font-size: 12px;
  color: ${themeGet('colors.fg.muted')};
`;

const Dot = styled.span`
  display: inline-block;
  width: 2px;
  height: 2px;
  border-radius: 50%;
  background-color: ${themeGet('colors.fg.disabled')};
`;

const ConfigurationListItem = styled.li`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const SectionHorizontal = styled(LTBox)`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ItemContent = styled(LTBox)`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const Img = styled.img`
  height: 16px;
  width: 16px;
`;

const HeaderRowGroup = styled.div`
  overflow-y: scroll;
  scrollbar-color: transparent transparent;
  scrollbar-width: thin;
  &::-webkit-scrollbar {
    visibility: hidden;
  }
`;

const RowGroup = styled.div`
  flex: 1;
  overflow-y: scroll;
  scrollbar-color: ${themeGet('colors.border.default')} transparent;
  scrollbar-width: thin;
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${themeGet('colors.border.default')};
    border-radius: 3px;
  }
`;

const NoDataWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
`;

const ConfigList = styled.ul`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const ShimmerBar = styled.div`
  height: 14px;
  width: ${({ $w }) => $w || '70%'};
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    ${themeGet('colors.border.default')} 25%,
    ${themeGet('colors.bg.muted')} 50%,
    ${themeGet('colors.border.default')} 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const columns = [
  { name: 'Timestamp', value: 'timestamp', width: 18 },
  { name: 'Status', value: 'status', width: 16 },
  { name: 'Configuration', value: 'configuration', width: 30 },
  { name: 'Duration', value: 'duration', width: 10 },
  { name: 'Flaky %', value: 'flaky', width: 9 },
  { name: 'User', value: 'user', width: 12 },
  { name: '', value: 'redirection', width: 5 }
];

const getCellValue = (testData, cell, parentTestId) => {
  let {
    browser = '',
    browser_version = '',
    create_timestamp = '',
    device = '',
    duration = '',
    flake_rate = 0,
    os = '',
    os_version = '',
    resolution = '',
    status = '',
    test_id = '',
    username = ''
  } = testData || {};

  const isFlakyTest = flake_rate && Number(flake_rate) > 0;
  const mobile = !!device;

  let browserIcon = getBrowserIcon(browser);
  let osIcon = getOsIcon(os);
  let displayBrowserLabel = browser_version
    ?.toLowerCase?.()
    .startsWith(browser?.toLowerCase?.())
    ? browser_version.slice(browser.length).trim()
    : '';
  let displayOsLabel = os_version
    ?.toLowerCase?.()
    .startsWith(os?.toLowerCase?.())
    ? os_version.slice(os.length).trim()
    : '';

  if (mobile) {
    browserIcon = phone;
    displayBrowserLabel = device || browser;
    displayOsLabel = os_version || '';
  }

  const { statusIconName } = getStatusIconAndClassPrefix(status);

  switch (cell) {
    case 'timestamp':
      return (
        <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR}>
          {formatTimestampToDateAndTime(create_timestamp) || '-'}
        </LTText>
      );

    case 'status':
      return (
        <SectionHorizontal>
          <span aria-hidden="true">
            <LTTestStatusLabel
              iconSrc={statusIconName || ''}
              status={status}
              showStatusText={false}
              width={12}
              height={12}
            />
          </span>
          <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR}>
            {TestStatusNameMap[status] || capitalize(status)}
          </LTText>
        </SectionHorizontal>
      );

    case 'flaky':
      return (
        <SectionHorizontal>
          <FlakyTestIcon color={isFlakyTest ? '#ED5F00' : '#8C959F'} />
          <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR}>
            {isFlakyTest ? `${Math.round(flake_rate * 100)}%` : '0'}
          </LTText>
        </SectionHorizontal>
      );

    case 'duration':
      return (
        <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR}>
          {convertTimeTaken(duration) || '-'}
        </LTText>
      );

    case 'configuration':
      return (
        <SectionHorizontal as="span">
          <ConfigList>
            <ConfigurationListItem>
              <LTTooltip
                direction={LTTooltip.Direction.BOTTOM}
                text={mobile ? device || 'Device' : capitalize(browser)}
              >
                <ItemContent>
                  {browserIcon && (
                    <Img
                      src={browserIcon}
                      alt={mobile ? 'device' : 'browser version'}
                    />
                  )}
                  <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR}>
                    {displayBrowserLabel}
                  </LTText>
                </ItemContent>
              </LTTooltip>
              <Dot />
            </ConfigurationListItem>
            <ConfigurationListItem>
              <LTTooltip
                direction={LTTooltip.Direction.BOTTOM}
                text={capitalize(os)}
              >
                <ItemContent>
                  {osIcon && <Img src={osIcon} alt="os version" />}
                  <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR}>
                    {displayOsLabel}
                  </LTText>
                </ItemContent>
              </LTTooltip>
              <Dot />
            </ConfigurationListItem>
            {resolution && (
              <ConfigurationListItem>
                <LTTooltip
                  direction={LTTooltip.Direction.BOTTOM}
                  text="Resolution"
                >
                  <ItemContent>
                    <Img src={resolutionIcon} alt="" aria-hidden="true" />
                    <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR}>
                      {resolution}
                    </LTText>
                  </ItemContent>
                </LTTooltip>
              </ConfigurationListItem>
            )}
          </ConfigList>
        </SectionHorizontal>
      );

    case 'user':
      return (
        <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR}>
          {username || '-'}
        </LTText>
      );

    case 'redirection':
      return (
        <LTIconButton
          aria-label="Redirect to Test"
          icon={() => <NewTab color="#333" />}
          onClick={() => {
            const params = new URLSearchParams(window.location.search);
            const details = params.get('details');
            const url = details
              ? `/test?testID=${test_id}&details=${details}`
              : `/test?testID=${test_id}`;
            window.open(url, '_blank');
          }}
          variant={LTIconButton.IconButtonVariants.INVISIBLE}
          size={LTIconButton.Size.SMALL}
        />
      );

    default:
      return null;
  }
};

const SCROLL_THRESHOLD = 80;

const HistoryList = ({
  data = [],
  isLoading = false,
  isSilentLoading = false,
  hasMore = false,
  parentTestId = '',
  onLoadMore
}) => {
  const loadingRef = useRef(false);

  const handleScroll = useCallback(
    (e) => {
      if (!hasMore || loadingRef.current || isSilentLoading) return;
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop - clientHeight <= SCROLL_THRESHOLD) {
        loadingRef.current = true;
        onLoadMore?.();
        // Reset after a tick so subsequent scrolls can trigger again
        setTimeout(() => {
          loadingRef.current = false;
        }, 300);
      }
    },
    [hasMore, isSilentLoading, onLoadMore]
  );

  if (data.length === 0 && !isLoading) {
    return (
      <NoDataWrapper>
        <LTText variant={LTText.TextVariant.SUBHEADER_REGULAR} color="fg.muted">
          No tests found
        </LTText>
      </NoDataWrapper>
    );
  }

  return isLoading ? (
    <LTLoader heading="Loading Test History" />
  ) : (
    <div
      role="table"
      tabIndex={-1}
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
        minWidth: 0
      }}
    >
      <HeaderRowGroup role="rowgroup">
        <ColumnRowWrapper
          role="row"
          style={{
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            fontWeight: 600,
            fontSize: '14px'
          }}
        >
          {columns.map((item, index) => (
            <ColumnCell
              key={index}
              role="columnheader"
              style={{ width: `${item.width}%` }}
            >
              {item?.name}
            </ColumnCell>
          ))}
        </ColumnRowWrapper>
      </HeaderRowGroup>
      <RowGroup role="rowgroup" onScroll={handleScroll}>
        {data.map((row, rowIndex) => (
          <DataRowWrapper
            role="row"
            key={row?.test_id ? `${row.test_id}-${rowIndex}` : rowIndex}
            $isSelectedTest={row?.test_id === parentTestId}
          >
            {columns.map((col, colIndex) => (
              <Cell
                key={colIndex}
                role="cell"
                style={{ width: `${col.width}%` }}
              >
                {getCellValue(row, col?.value, parentTestId)}
              </Cell>
            ))}
          </DataRowWrapper>
        ))}
        {isSilentLoading &&
          Array.from({ length: 2 }).map((_, i) => (
            <DataRowWrapper
              role="row"
              key={`shimmer-${i}`}
              style={{ padding: '4px 0' }}
            >
              {columns.map((col, ci) => (
                <Cell
                  key={ci}
                  style={{ width: `${col.width}%`, padding: '10px 12px' }}
                >
                  <ShimmerBar
                    $w={
                      col.value === 'redirection'
                        ? '24px'
                        : col.value === 'configuration'
                        ? '85%'
                        : '60%'
                    }
                  />
                </Cell>
              ))}
            </DataRowWrapper>
          ))}
      </RowGroup>
    </div>
  );
};

export default HistoryList;
